import type { LocationQuery } from 'vue-router'

/** UTM query params that may be propagated across internal navigation. */
export const ALLOWED_UTM_KEYS = [
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
] as const

export type UtmKey = typeof ALLOWED_UTM_KEYS[number]

export type AllowedUtmQuery = Partial<Record<UtmKey, string>>

/**
 * Normalizes a Vue Router query value to a single trimmed string.
 *
 * @param value - Raw query value from `route.query[key]` (`string`, `string[]`, `null`, or `undefined`)
 * @returns Trimmed string, or `undefined` when empty / absent
 */
function queryValueToString(value: LocationQuery[UtmKey]): string | undefined {
  if (value == null)
    return undefined
  const raw = Array.isArray(value) ? value[0] : value
  const trimmed = String(raw).trim()
  return trimmed || undefined
}

/**
 * Extracts only allowed UTM params from a route query.
 * Ignores every other query key (e.g. shopping_cart_id, edit).
 *
 * @param query - Full `route.query` from the current or previous route
 * @returns Object with only present allowed UTMs, e.g. `{ utm_source: 'google' }`; `{}` when none
 */
export function getAllowedUtmQuery(query: LocationQuery): AllowedUtmQuery {
  const result: AllowedUtmQuery = {}

  for (const key of ALLOWED_UTM_KEYS) {
    const value = queryValueToString(query[key])
    if (value)
      result[key] = value
  }

  return result
}

/**
 * Reads allowed UTMs from the current browser URL.
 * Covers cases where `route.query` is out of sync (e.g. after `history.replaceState` on login).
 */
function getAllowedUtmQueryFromWindow(): AllowedUtmQuery {
  if (typeof window === 'undefined' || !window.location.search)
    return {}

  const params = new URLSearchParams(window.location.search)
  const query: LocationQuery = {}

  for (const key of ALLOWED_UTM_KEYS) {
    const value = params.get(key)?.trim()
    if (value)
      query[key] = value
  }

  return getAllowedUtmQuery(query)
}

/**
 * Collects UTMs from the browser URL and the previous route.
 * Window is checked first so UTMs survive when `from.query` is empty.
 */
export function collectUtmsToPropagate(fromQuery: LocationQuery): AllowedUtmQuery {
  return {
    ...getAllowedUtmQueryFromWindow(),
    ...getAllowedUtmQuery(fromQuery),
  }
}

/**
 * Merges allowed UTMs into the destination query without overwriting
 * params already set on the target route (destination wins).
 *
 * @param toQuery - Destination `route.query` (params defined here are kept)
 * @param utms - Allowed UTMs to propagate (typically from `collectUtmsToPropagate(from.query)`)
 * @returns New query with destination params plus missing UTMs filled in
 */
export function mergeRouteQueryWithUtms(
  toQuery: LocationQuery,
  utms: AllowedUtmQuery,
): LocationQuery {
  const merged: LocationQuery = { ...toQuery }

  for (const key of ALLOWED_UTM_KEYS) {
    if (merged[key] != null && merged[key] !== '')
      continue

    const utmValue = utms[key]
    if (utmValue)
      merged[key] = utmValue
  }

  return merged
}

/**
 * Checks whether merging UTMs would change the destination query.
 * Used by the router guard to decide if a redirect is needed (avoids infinite loops).
 *
 * @param toQuery - Original destination query before merge
 * @param mergedQuery - Result of `mergeRouteQueryWithUtms(toQuery, utms)`
 * @returns `true` if at least one allowed UTM was added; `false` if nothing new to propagate
 */
export function hasMissingAllowedUtms(
  toQuery: LocationQuery,
  mergedQuery: LocationQuery,
): boolean {
  return ALLOWED_UTM_KEYS.some((key) => {
    const mergedValue = queryValueToString(mergedQuery[key])
    const toValue = queryValueToString(toQuery[key])
    return Boolean(mergedValue && !toValue)
  })
}
